import {
  Drawer,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { TFunction } from 'i18next';
import React, { memo } from 'react';
import { WorkspaceRoutes } from 'types/common';
import { DRAWER_ORDERS_WIDTH } from 'utils/constants';

import CloseSidemnu from './close';
import OpenSidemnu from './open';
import { CurrentUser } from 'contexts/userContext';

type SidemenuProps = {
  t: TFunction;
  sidemenuOpen: boolean;
  setSidemenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleViewChange: (view: WorkspaceRoutes, param?: string) => void;
  orderType: string;
  transactionType: string;
  currentUser: CurrentUser | null;
};

const useStyles = makeStyles((theme: any) => ({
  drawer: {
    width: DRAWER_ORDERS_WIDTH,
    // Hide permanently on mobile (below md breakpoint)
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
    // On desktop: drawer is fixed and positioned below the AppBar
    [theme.breakpoints.up('md')]: {
      position: 'fixed',
      left: 0,
      top: 81,
      height: 'calc(100vh - 81px)',
      zIndex: theme.zIndex.drawer - 1,
    },
  },
  drawerOpen: {
    width: DRAWER_ORDERS_WIDTH,
    scrollbarColor: `${theme.palette.tab.offselected} ${theme.palette.primary.main}`,
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      // theme.palette.primary.main
      background: theme.palette.primary.main,
      width: theme.spacing(0.625),
      borderRadius: theme.spacing(0.625)
    }, /* Chrome */
    '&::-webkit-scrollbar-track': { borderRadius: theme.spacing(0.625) },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.tab.offselected,
      borderRadius: theme.spacing(0.625),
    },
  },
  drawerClose: {
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    width: parseFloat(theme.spacing(7)) + 1,
    [theme.breakpoints.up('sm')]: { width: parseFloat(theme.spacing(9)) + 1 },
  },
  drawerPaperOpen: {
    width: DRAWER_ORDERS_WIDTH,
    backgroundColor: theme.palette.primary.main,
    top: 81,
    height: 'calc(100vh - 81px)',
  },
  drawerPaperClose: {
    width: parseFloat(theme.spacing(7)) + 1,
    backgroundColor: theme.palette.primary.main,
    top: 81,
    height: 'calc(100vh - 81px)',
  },
  drawerPaper: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
}));

const Sidemenu = ({
  t,
  sidemenuOpen,
  setSidemenuOpen,
  handleViewChange,
  orderType,
  transactionType,
  currentUser
}: SidemenuProps) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  if (!isDesktop) {
    return null;
  }

  return (
    <Drawer
      variant="permanent"
      className={clsx(classes.drawer, {
        [classes.drawerOpen]: sidemenuOpen,
        [classes.drawerClose]: !sidemenuOpen,
      })}
      classes={{
        paper: clsx(classes.drawerPaper, {
          [classes.drawerPaperOpen]: sidemenuOpen,
          [classes.drawerPaperClose]: !sidemenuOpen,
          [classes.drawerOpen]: sidemenuOpen,
          [classes.drawerClose]: !sidemenuOpen,
        })
      }}
    >
      {sidemenuOpen ? (
        <OpenSidemnu
          t={t}
          setSidemenuOpen={setSidemenuOpen}
          handleViewChange={handleViewChange}
          orderType={orderType}
          transactionType={transactionType}
          currentUser={currentUser}
        />
      ) : (
        <CloseSidemnu
          t={t}
          setSidemenuOpen={setSidemenuOpen}
          handleViewChange={handleViewChange}
          orderType={orderType}
          currentUser={currentUser}
        />
      )}
    </Drawer>
  );
};

export default memo(Sidemenu);
